import { useQuery } from "@apollo/client";

import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import Placeholder from "@components/placeholder/placeholder";
import { classNames } from "@helpers/css";

import searchSkillsQuery from "../graphql/search-skills-query";
import { SkillItem } from "../wysiwyg/skill-commands-extension";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: number | null;
  /** Called with the skill slug (no leading slash) when the user picks one. */
  onPickSkill: (slug: string) => void;
};

const SECTION_ORDER: Array<{
  label: string;
  visibility: SkillItem["visibility"];
}> = [
  { label: "Your skills", visibility: "PERSONAL" },
  { label: "Workspace skills", visibility: "WORKSPACE" },
  { label: "Platform skills", visibility: "PLATFORM" },
];

const VISIBILITY_BADGE: Record<
  SkillItem["visibility"],
  { label: string; className: string }
> = {
  PERSONAL: { label: "Personal", className: "bg-emerald-100 text-emerald-700" },
  WORKSPACE: { label: "Workspace", className: "bg-blue-100 text-blue-700" },
  PLATFORM: { label: "Platform", className: "bg-purple-200 text-purple-700" },
};

const SkillsDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  organizationId,
  onPickSkill,
}) => {
  const { data, loading } = useQuery<any>(searchSkillsQuery, {
    fetchPolicy: "cache-and-network",
    skip: !isOpen,
    variables: { organizationId },
  });

  if (!isOpen) return null;

  const items: SkillItem[] = (data?.searchSkills ?? []).filter(
    (s: SkillItem) => s.slug !== "user-observations",
  );

  const handlePick = (slug: string) => {
    onPickSkill(slug);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      aria-label="Skills dialog"
      dialogClassName="md:max-w-3xl"
    >
      <div className="p-6 bg-white rounded-md">
        <div className="mb-4">
          <ModalTitle onClose={onClose}>Skills</ModalTitle>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Click a skill to load{" "}
          <code className="bg-gray-100 px-1 rounded">/slug</code> into the chat
          input. You can also type{" "}
          <code className="bg-gray-100 px-1 rounded">/</code> in the input to
          search.
        </p>

        {loading && items.length === 0 ? (
          <div className="flex justify-center my-8">
            <Loading mini size="5" />
          </div>
        ) : items.length === 0 ? (
          <Placeholder>No skills available.</Placeholder>
        ) : (
          <div className="border rounded overflow-hidden max-h-[60vh] overflow-y-auto">
            {SECTION_ORDER.map((section) => {
              const rows = items.filter(
                (item) => item.visibility === section.visibility,
              );
              if (rows.length === 0) return null;
              return (
                <div key={section.visibility}>
                  <div className="sticky top-0 bg-gray-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b">
                    {section.label}
                  </div>
                  {rows.map((item) => {
                    const badge = VISIBILITY_BADGE[item.visibility];
                    return (
                      <button
                        type="button"
                        key={`${item.__typename}-${item.slug}`}
                        onClick={() => handlePick(item.slug)}
                        className="block w-full text-left px-4 py-2 border-b last:border-b-0 hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-sm text-blue-link truncate">
                            <span aria-hidden="true">/</span>
                            {item.slug}
                          </span>
                          <span
                            className={classNames(
                              "shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium leading-none",
                              badge.className,
                            )}
                          >
                            {badge.label}
                          </span>
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {item.description}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SkillsDialog;
